import { PlusOutlined, SyncOutlined, UserAddOutlined } from '@ant-design/icons';
import { Button, Drawer, PageHeader, Tabs } from 'antd';
import type { Key } from 'react';
import React from 'react';
import PageHeaderTitle from '../../../components/page-header';
import { DrawerWidth } from '../../../config/dimensions';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { ActionOption, DrawerState, ModuleName } from '../../../core/models/enum';
import { isUserAllowed } from '../../../helpers/CheckPermissionHelper';
import { errorWithUserMessage, success } from '../../../helpers/NotificationHelper';
import { getNewRoute, getSubPageInitialProperties } from '../../../helpers/RoutingHelper';
import { transformCompetitionTeamsToTeams } from '../../../helpers/TransformHelper';
import {
    ApiException,
    CompetitionDetailVm,
    CompetitionsClient,
    CompetitionTeamsClient,
    CompetitionTeamVm,
    StandingsVm,
    TeamVm,
} from '../../../utils/api';
import { applyBatchOfficialsToMatches } from '../../matches/batchMatchOfficialsApply';
import BatchMatchOfficialsDrawer, {
    BatchOfficialsApplyMode,
    OfficialRolePair,
} from '../../matches/BatchMatchOfficialsDrawer';
import MatchForm from '../../matches/match-form';
import MatchTable from '../../matches/match-table';
import MatchSideDrawers, { MatchSideDrawerOpen } from '../../matches/match-table/MatchSideDrawers';
import TeamTable from '../../teams/team-table';
import CompetitionTeamForm from '../competition-team';
import CompetitionDetails from './competition-details';
import CompetitionMatchStagesTab from './CompetitionMatchStagesTab';
import { Props } from './index';
import StandingsTable from './standings-table';

const { TabPane } = Tabs;

interface State {
    competition?: CompetitionDetailVm;
    competitionStandings?: StandingsVm[];
    selectedCompetitionTeam?: CompetitionTeamVm;
    teams?: TeamVm[];
    activeTabKey: string;
    isEditMode: boolean;
    id?: string;
    drawerState: DrawerState;
    loading: boolean;
    isUserAllowedToCreateMatch: boolean;
    isUserAllowedToCreateTeam: boolean;
    isUserAllowedToUpdateCompetition: boolean;
    allowBatchMatchUpdate: boolean;
    batchSelectMode: boolean;
    batchSelectedRowKeys: Key[];
    batchOfficialsDrawerVisible: boolean;
    matchSideDrawer: MatchSideDrawerOpen;
}

class CompetitionPage extends React.PureComponent<Props, State> {
    private teamsTableRef: any;
    private matchesTableRef: any;

    public constructor(props: Props) {
        super(props);

        const {
            location: { pathname },
        } = window;
        this.teamsTableRef = React.createRef();
        this.matchesTableRef = React.createRef();

        const initialProperties = getSubPageInitialProperties(
            pathname,
            'competitions',
            props.match,
            Routes.ROUTE_COMPETITIONS_READ,
            Routes.ROUTE_COMPETITIONS_EDIT
        );

        this.state = {
            activeTabKey: initialProperties.activeTabKey || Routes.ROUTE_COMPETITIONS_READ,
            id: initialProperties.initialEntityId,
            isEditMode: initialProperties.isInitialEditMode,
            drawerState:
                initialProperties.activeTabKey &&
                initialProperties.activeTabKey !== Routes.ROUTE_COMPETITIONS_READ &&
                initialProperties.isInitialEditMode
                    ? DrawerState.Edit
                    : DrawerState.Closed,
            loading: true,
            isUserAllowedToCreateMatch: false,
            isUserAllowedToCreateTeam: false,
            isUserAllowedToUpdateCompetition: false,
            allowBatchMatchUpdate: false,
            batchSelectMode: false,
            batchSelectedRowKeys: [],
            batchOfficialsDrawerVisible: false,
            matchSideDrawer: null,
        };
    }

    public componentDidMount = async (): Promise<void> => {
        this.getPermissions();
        this.getCompetitionDetails();
        await this.getStandings();
    };

    public componentDidUpdate = (prevProps: Props): void => {
        const {
            match: { path },
        } = this.props;

        if (path !== prevProps.match.path) {
            this.updateStateAfterRouteChanges();
        }
    };

    private getStandings = async (): Promise<void> => {
        const {
            match: {
                params: { competitionId },
            },
        } = this.props;

        const result = await new CompetitionsClient().getStandings(+competitionId);
        this.setState({
            competitionStandings: result,
        });
    };

    private updateStandings = async () => {
        this.setState({
            loading: true,
        });

        const {
            match: {
                params: { competitionId },
            },
        } = this.props;

        const result = await new CompetitionsClient().updateStandings(+competitionId);
        this.setState(
            {
                competitionStandings: result,
            },
            () => {
                this.setState({
                    loading: false,
                });
            }
        );
    };

    private getPermissions = (): void => {
        const { userProfile } = this.props;

        this.setState({
            isUserAllowedToCreateMatch: isUserAllowed(
                userProfile,
                ModuleName.Matches,
                ActionOption.Create
            ),
            isUserAllowedToCreateTeam: isUserAllowed(
                userProfile,
                ModuleName.CompetitionTeams,
                ActionOption.Create
            ),
            isUserAllowedToUpdateCompetition: isUserAllowed(
                userProfile,
                ModuleName.Competitions,
                ActionOption.Update
            ),
            allowBatchMatchUpdate: isUserAllowed(
                userProfile,
                ModuleName.Matches,
                ActionOption.Update
            ),
        });
    };

    private updateStateAfterRouteChanges = (): void => {
        const { match } = this.props;
        const { competition, selectedCompetitionTeam } = this.state;
        const {
            location: { pathname },
        } = window;

        const initialProperties = getSubPageInitialProperties(
            pathname,
            'competitions',
            match,
            Routes.ROUTE_COMPETITIONS_READ,
            Routes.ROUTE_COMPETITIONS_EDIT
        );

        var newSelectedCompetitionTeam = match.params.teamId
            ? competition?.competitionTeams.find(
                  (ct: CompetitionTeamVm): boolean => ct.teamId.toString() === match.params.teamId
              )
            : selectedCompetitionTeam;

        this.setState({
            activeTabKey: initialProperties.activeTabKey || Routes.ROUTE_COMPETITIONS_READ,
            id: initialProperties.initialEntityId,
            isEditMode: initialProperties.isInitialEditMode,
            drawerState:
                initialProperties.activeTabKey &&
                initialProperties.activeTabKey !== Routes.ROUTE_COMPETITIONS_READ &&
                initialProperties.isInitialEditMode
                    ? DrawerState.Edit
                    : DrawerState.Closed,
            selectedCompetitionTeam: newSelectedCompetitionTeam,
        });
    };

    private getCompetitionDetails = async (section?: string): Promise<void> => {
        const {
            match: {
                params: { competitionId, teamId },
            },
        } = this.props;

        if (competitionId) {
            const competitionClient = new CompetitionsClient();
            const competitionDetails = await competitionClient.getById(parseInt(competitionId, 10));

            if (teamId) {
                var selectedCompetitionTeam = competitionDetails.competitionTeams.find(
                    (tc: CompetitionTeamVm): boolean => tc.teamId.toString() === teamId
                );
            }

            this.setState({
                competition: competitionDetails,
                selectedCompetitionTeam,
            });

            if (competitionDetails) {
                const teams = transformCompetitionTeamsToTeams(competitionDetails.competitionTeams);
                this.setState({
                    teams,
                });
            }
        }

        if (section === 'teams' && this.teamsTableRef && this.teamsTableRef.current) {
            this.teamsTableRef.current.getTeams();
        } else if (section === 'matches' && this.matchesTableRef && this.matchesTableRef.current) {
            this.matchesTableRef.current.getMatches();
        }

        this.setState({
            loading: false,
        });
    };

    private handleTabClick = (route: string): void => {
        const {
            history,
            match: {
                params: { competitionId },
            },
        } = this.props;

        this.setState({
            activeTabKey: route,
            isEditMode: false,
        });

        const newRoute = route.replace(ReplaceStrings.COMPETITION_ID, competitionId);

        history.push(newRoute);
    };

    private handleDrawerMode = (drawerState: DrawerState, section?: string): void => {
        const {
            history,
            match: {
                params: { competitionId },
            },
        } = this.props;

        this.setState({
            drawerState,
        });

        if (section === 'teams') {
            history.push(
                getNewRoute(
                    Routes.ROUTE_COMPETITION_TEAMS,
                    Routes.ROUTE_COMPETITION_TEAMS,
                    Routes.ROUTE_COMPETITION_TEAMS_CREATE,
                    drawerState,
                    ReplaceStrings.COMPETITION_ID,
                    competitionId
                )
            );
        } else if (section === 'matches') {
            history.push(
                getNewRoute(
                    Routes.ROUTE_COMPETITION_MATCHES,
                    Routes.ROUTE_COMPETITION_MATCHES,
                    Routes.ROUTE_COMPETITION_MATCHES_CREATE,
                    drawerState,
                    ReplaceStrings.COMPETITION_ID,
                    competitionId
                )
            );
        }
    };

    private handleCompTeamDelete = async (id?: number): Promise<void> => {
        const { competition } = this.state;
        if (id) {
            try {
                const competitionTeams = competition?.competitionTeams || [];
                const competitionTeam = competitionTeams.find(
                    (ct: CompetitionTeamVm): boolean => ct.teamId === id
                );

                if (competitionTeam) {
                    const client = new CompetitionTeamsClient();
                    const result = await client.delete(competitionTeam.id);

                    if (result) {
                        success('Competition team successfully deleted.');
                        await this.getCompetitionDetails();
                        if (this.teamsTableRef && this.teamsTableRef.current) {
                            this.teamsTableRef.current.getTeams();
                        }
                    }
                }
            } catch (error) {
                if (error instanceof ApiException) {
                    errorWithUserMessage(error.response);
                } else {
                    errorWithUserMessage('Error deleting data.');
                }
            }
        }
    };

    private startBatchOfficials = (): void => {
        this.setState({
            batchSelectMode: true,
            batchSelectedRowKeys: [],
            batchOfficialsDrawerVisible: false,
        });
    };

    private cancelBatchOfficials = (): void => {
        this.setState({
            batchSelectMode: false,
            batchSelectedRowKeys: [],
            batchOfficialsDrawerVisible: false,
        });
    };

    private openBatchOfficialsDrawer = (): void => {
        if (this.state.batchSelectedRowKeys.length === 0) {
            return;
        }
        this.setState({ batchOfficialsDrawerVisible: true });
    };

    private closeBatchOfficialsDrawer = (): void => {
        this.setState({ batchOfficialsDrawerVisible: false });
    };

    private applyBatchOfficials = async (
        pairs: OfficialRolePair[],
        mode: BatchOfficialsApplyMode
    ): Promise<void> => {
        const { batchSelectedRowKeys } = this.state;
        const matchIds = batchSelectedRowKeys.map((k) => Number(k));
        const { ok, failed } = await applyBatchOfficialsToMatches(matchIds, pairs, mode);

        if (failed === 0) {
            success(`Officials updated on ${ok} match(es).`);
        } else if (ok === 0) {
            errorWithUserMessage('Could not update officials for any match.');
        } else {
            success(`Officials updated on ${ok} match(es); ${failed} failed.`);
        }

        this.setState({
            batchSelectMode: false,
            batchSelectedRowKeys: [],
            batchOfficialsDrawerVisible: false,
        });
        await this.getCompetitionDetails('matches');
    };

    private openMatchOfficialsDrawer = (matchId: number): void => {
        this.setState({ matchSideDrawer: { type: 'officials', matchId } });
    };

    private openMatchDetailsDrawer = (matchId: number): void => {
        this.setState({ matchSideDrawer: { type: 'details', matchId } });
    };

    private closeMatchSideDrawer = (): void => {
        this.setState({ matchSideDrawer: null });
    };

    public render(): React.ReactElement {
        const { userProfile } = this.props;
        const {
            activeTabKey,
            competition,
            competitionStandings,
            teams,
            isEditMode,
            drawerState,
            loading,
            selectedCompetitionTeam,
            isUserAllowedToCreateMatch,
            isUserAllowedToCreateTeam,
            isUserAllowedToUpdateCompetition,
            allowBatchMatchUpdate,
            batchSelectMode,
            batchSelectedRowKeys,
            batchOfficialsDrawerVisible,
            matchSideDrawer,
        } = this.state;

        const matchesHeaderExtra: React.ReactElement[] = [];
        if (batchSelectMode) {
            matchesHeaderExtra.push(
                <Button key="batch-cancel" onClick={this.cancelBatchOfficials}>
                    Cancel batch
                </Button>
            );
            matchesHeaderExtra.push(
                <Button
                    key="batch-next"
                    type="primary"
                    disabled={batchSelectedRowKeys.length === 0}
                    onClick={this.openBatchOfficialsDrawer}
                >
                    Next: choose officials
                </Button>
            );
        } else if (allowBatchMatchUpdate) {
            matchesHeaderExtra.push(
                <Button
                    key="batch-start"
                    icon={<UserAddOutlined />}
                    onClick={this.startBatchOfficials}
                >
                    Batch add officials
                </Button>
            );
        }
        if (isUserAllowedToCreateMatch) {
            matchesHeaderExtra.push(
                <Button
                    key="add-match"
                    type="primary"
                    style={{
                        zIndex: 10,
                        float: 'right',
                    }}
                    onClick={(): void => this.handleDrawerMode(DrawerState.Edit, 'matches')}
                    title={
                        !teams || teams.length < 2
                            ? 'Add more teams to the competition before creating a match'
                            : ''
                    }
                    disabled={!teams || teams.length < 2}
                >
                    <PlusOutlined className="button-icon-display" />
                    Add match
                </Button>
            );
        }

        return (
            <div>
                <PageHeader
                    title={
                        <PageHeaderTitle
                            headerTitle={
                                competition
                                    ? competition.nameUniversal || competition.name || ''
                                    : ''
                            }
                            headerPhoto={
                                competition && competition.logo ? competition.logo : undefined
                            }
                            tableName="competition"
                            imgHeight={45}
                        />
                    }
                    style={{ padding: '16px 0px' }}
                />
                <div className="tab-padding">
                    <div
                        style={{
                            overflow: 'hidden',
                            width: '100%',
                            transition: 'width .3s cubic-bezier',
                        }}
                    >
                        <Tabs
                            style={{ width: '100%' }}
                            activeKey={activeTabKey}
                            onTabClick={(key: string): void => this.handleTabClick(key)}
                        >
                            <TabPane tab="Details" key={Routes.ROUTE_COMPETITIONS_READ}>
                                <div>
                                    {competition && (
                                        <CompetitionDetails
                                            userProfile={userProfile}
                                            isInitialEdit={isEditMode}
                                            competition={competition}
                                            refreshAfterSave={this.getCompetitionDetails}
                                        />
                                    )}
                                </div>
                            </TabPane>
                            <TabPane tab="Teams" key={Routes.ROUTE_COMPETITION_TEAMS}>
                                <div>
                                    <PageHeader
                                        title=""
                                        extra={
                                            isUserAllowedToCreateTeam
                                                ? [
                                                      <Button
                                                          type="primary"
                                                          style={{
                                                              zIndex: 10,
                                                              float: 'right',
                                                          }}
                                                          onClick={(): void =>
                                                              this.handleDrawerMode(
                                                                  DrawerState.Edit,
                                                                  'teams'
                                                              )
                                                          }
                                                      >
                                                          <PlusOutlined className="button-icon-display" />
                                                          Add team
                                                      </Button>,
                                                  ]
                                                : []
                                        }
                                        style={{ padding: '16px 0px' }}
                                    />
                                    {competition && teams?.length ? (
                                        <TeamTable
                                            userProfile={userProfile}
                                            teamsFromProps={teams}
                                            onDelete={this.handleCompTeamDelete}
                                            wrappedComponentRef={this.teamsTableRef}
                                            moduleName={ModuleName.CompetitionTeams}
                                            shouldDisplayEditDelete
                                            competitionId={competition.id}
                                        />
                                    ) : (
                                        <div>No team competes in this competition.</div>
                                    )}
                                </div>
                            </TabPane>
                            <TabPane tab="Matches" key={Routes.ROUTE_COMPETITION_MATCHES}>
                                <div>
                                    <PageHeader
                                        title=""
                                        extra={
                                            matchesHeaderExtra.length
                                                ? matchesHeaderExtra
                                                : undefined
                                        }
                                        style={{ padding: '16px 0px' }}
                                    />
                                    {batchSelectMode && (
                                        <div style={{ marginBottom: 12, color: '#595959' }}>
                                            Step 1: tick the matches to update, then click
                                            &quot;Next: choose officials&quot;.
                                        </div>
                                    )}
                                    {competition &&
                                    competition.matches &&
                                    competition.matches.length > 0 ? (
                                        <MatchTable
                                            userProfile={userProfile}
                                            matchesFromProps={competition.matches}
                                            wrappedComponentRef={this.matchesTableRef}
                                            onMatchOfficialsDrawerOpen={
                                                this.openMatchOfficialsDrawer
                                            }
                                            onMatchDetailsDrawerOpen={this.openMatchDetailsDrawer}
                                            batchSelectionMode={batchSelectMode}
                                            batchSelectedRowKeys={batchSelectedRowKeys}
                                            onBatchSelectedRowKeysChange={(keys: Key[]): void =>
                                                this.setState({ batchSelectedRowKeys: keys })
                                            }
                                        />
                                    ) : (
                                        <div>There are no matches in this competition.</div>
                                    )}
                                </div>
                            </TabPane>
                            <TabPane tab="Standings" key={Routes.ROUTE_COMPETITION_STANDINGS}>
                                <div>
                                    <PageHeader
                                        title=""
                                        extra={
                                            isUserAllowedToCreateMatch
                                                ? [
                                                      <Button
                                                          type="primary"
                                                          style={{
                                                              zIndex: 10,
                                                              float: 'right',
                                                          }}
                                                          onClick={this.updateStandings}
                                                      >
                                                          <SyncOutlined
                                                              spin={loading}
                                                              className="button-icon-display"
                                                          />
                                                          Update Standings
                                                      </Button>,
                                                  ]
                                                : []
                                        }
                                        style={{ padding: '16px 0px' }}
                                    />
                                    {competition &&
                                    competitionStandings &&
                                    competitionStandings.length > 0 ? (
                                        competitionStandings.map((cs) => (
                                            <StandingsTable
                                                userProfile={userProfile}
                                                standings={cs}
                                                isLoading={loading}
                                            />
                                        ))
                                    ) : (
                                        <div>
                                            No standings could be loaded for this competition.
                                        </div>
                                    )}
                                </div>
                            </TabPane>
                            <TabPane tab="Match stages" key={Routes.ROUTE_COMPETITION_MATCH_STAGES}>
                                <div>
                                    {competition && (
                                        <CompetitionMatchStagesTab
                                            competition={competition}
                                            canEdit={isUserAllowedToUpdateCompetition}
                                            onSaved={this.getCompetitionDetails}
                                        />
                                    )}
                                </div>
                            </TabPane>
                        </Tabs>
                    </div>
                </div>
                <Drawer
                    title={
                        activeTabKey === Routes.ROUTE_COMPETITION_TEAMS
                            ? selectedCompetitionTeam
                                ? 'Edit team'
                                : 'Add team to competition'
                            : 'Add match'
                    }
                    visible={!!drawerState}
                    onClose={(): void =>
                        this.handleDrawerMode(
                            DrawerState.Closed,
                            activeTabKey === Routes.ROUTE_COMPETITION_TEAMS ? 'teams' : 'matches'
                        )
                    }
                    width={DrawerWidth}
                    destroyOnClose
                >
                    {activeTabKey === Routes.ROUTE_COMPETITION_TEAMS &&
                        competition &&
                        competition.id &&
                        !loading && (
                            <CompetitionTeamForm
                                competitionTeam={selectedCompetitionTeam}
                                handleClose={(): void => {
                                    this.handleDrawerMode(DrawerState.Closed, 'teams');
                                    this.setState({
                                        selectedCompetitionTeam: undefined,
                                    });
                                }}
                                refreshAfterSave={(): Promise<void> =>
                                    this.getCompetitionDetails('teams')
                                }
                                initialValues={{
                                    competitionId: competition.id,
                                    usedTeamIds:
                                        teams && teams.length > 0
                                            ? teams.map((t: TeamVm): number => t.id)
                                            : undefined,
                                    isInternational: competition.isInternational,
                                    federationId: competition.federationId,
                                }}
                            />
                        )}
                    {activeTabKey === Routes.ROUTE_COMPETITION_MATCHES &&
                        competition &&
                        competition.id &&
                        !loading && (
                            <MatchForm
                                handleClose={(): void =>
                                    this.handleDrawerMode(DrawerState.Closed, 'matches')
                                }
                                refreshAfterSave={(): Promise<void> =>
                                    this.getCompetitionDetails('matches')
                                }
                                initialValues={{
                                    competitionId: competition.id,
                                    teams: teams,
                                }}
                            />
                        )}
                </Drawer>
                <MatchSideDrawers
                    drawer={matchSideDrawer}
                    onClose={this.closeMatchSideDrawer}
                    onSaved={(): Promise<void> => this.getCompetitionDetails('matches')}
                    userProfile={this.props.userProfile}
                />
                <Drawer
                    title="Batch add officials — step 2"
                    visible={batchOfficialsDrawerVisible}
                    onClose={this.closeBatchOfficialsDrawer}
                    width={DrawerWidth}
                    destroyOnClose
                >
                    <BatchMatchOfficialsDrawer
                        visible={batchOfficialsDrawerVisible}
                        selectedMatchCount={batchSelectedRowKeys.length}
                        onClose={this.closeBatchOfficialsDrawer}
                        onApply={this.applyBatchOfficials}
                    />
                </Drawer>
            </div>
        );
    }
}

export default CompetitionPage;
